import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FlaskConical, Code, X } from 'lucide-react';
import { CertificationInterestForm } from '../components/forms/CertificationInterestForm';
import { WhitepaperForm } from '../components/forms/WhitepaperForm';

const HomePage = () => {
  const [showCertForm, setShowCertForm] = useState(false);
  const [showWhitepaperForm, setShowWhitepaperForm] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const visited = localStorage.getItem('safe_visited');
    if (!visited) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        localStorage.setItem('safe_visited', 'true');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white section-padding" data-testid="hero-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-8">
              AI systems are being deployed into child-facing environments without independent verification.
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-4">
              Safety is being claimed. Not proven.
            </p>
            <p className="text-xl md:text-2xl text-white font-semibold mb-10">
              SAFE is the standard that proves it.
            </p>
            <p className="text-sm uppercase tracking-widest text-slate-400 mb-10">
              Independent definition. Independent verification. Structural separation.
            </p>
            <Link
              to="/governance"
              className="btn-primary-light"
              data-testid="btn-view-standard"
            >
              View the Standard
            </Link>
          </div>
        </div>
      </section>

      {/* What's Happening */}
      <section className="section-padding bg-slate-50 border-t border-slate-200" data-testid="problem-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">What's Happening</p>
            <h2 className="heading-2 mb-8">
              AI safety today is defined by guidance, policies, and internal testing.
            </h2>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">Governments issue guardrails.</p>
              <p className="text-lg text-slate-700">Companies define their own standards.</p>
              <p className="text-lg text-slate-700">Systems are tested by the people who build them.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold mt-8">
              This is not verification.
            </p>
          </div>
        </div>
      </section>

      {/* What SAFE Is */}
      <section className="section-padding bg-white border-t border-slate-200" data-testid="what-safe-is-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">What SAFE Is</p>
            <h2 className="heading-2 mb-8">
              SAFE is a verification standard.
            </h2>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">It defines what safety is.</p>
              <p className="text-lg text-slate-700">It verifies whether systems meet it.</p>
              <p className="text-lg text-slate-700">It enforces separation between definition, implementation, and verification.</p>
            </div>
          </div>

          {/* Structure: 3 blocks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            <div className="bg-slate-900 text-white p-8 border-l-4 border-slate-900" data-testid="block-safe">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE</h3>
              <p className="text-slate-300">Defines the standard</p>
            </div>
            <div className="bg-cyan-700 text-white p-8 border-l-4 border-cyan-700" data-testid="block-labs">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <FlaskConical className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE Labs</h3>
              <p className="text-cyan-100">Verifies systems independently</p>
            </div>
            <div className="bg-amber-600 text-white p-8 border-l-4 border-amber-600" data-testid="block-sail">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Code className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAIL</h3>
              <p className="text-amber-100">Implements the standard within systems</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="section-padding bg-slate-900 text-white" data-testid="gap-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">The Gap</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-8">
              Every high-risk system has independent verification.
            </h2>
            <div className="space-y-3 mb-8">
              <p className="text-lg text-slate-300">Aviation.</p>
              <p className="text-lg text-slate-300">Pharmaceuticals.</p>
              <p className="text-lg text-slate-300">Infrastructure.</p>
            </div>
            <p className="text-xl text-white font-semibold">
              AI does not.
            </p>
          </div>
        </div>
      </section>

      {/* Consequence */}
      <section className="section-padding bg-slate-50 border-t border-slate-200" data-testid="consequence-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Consequence</p>
            <h2 className="heading-2 mb-8">
              Without independent verification:
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Safety claims cannot be trusted.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Harm is addressed after it occurs.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Accountability does not exist.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section className="section-padding bg-white border-t border-slate-200" data-testid="cta-section">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
            If your system is safe —
          </h2>
          <p className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">
            prove it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowCertForm(true)}
              className="btn-primary"
              data-testid="btn-request-certification"
            >
              Request Certification
            </button>
            <Link
              to="/governance"
              className="btn-secondary"
              data-testid="btn-explore-standard"
            >
              Explore the Standard
            </Link>
          </div>
        </div>
      </section>

      {/* Whitepaper */}
      <section id="whitepaper" className="py-16 bg-slate-900 text-white" data-testid="whitepaper-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="caption text-cyan-400 mb-4">Governance Whitepaper</p>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
                The complete SAFE standard.
              </h2>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Why current AI safety systems fail — and the structural gap they leave</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">The separation principle — governance, certification, and implementation</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">SAFE-001 risk taxonomy — ten behavioural harm classifications (P1–P10)</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Council structure, validation gates, and capture prevention architecture</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Regulatory alignment — EU, UK, US, Australia, and UN frameworks</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Five-phase adoption pathway from formation to recognised standard</p>
                </div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <button
                onClick={() => setShowWhitepaperForm(true)}
                className="bg-white text-slate-900 px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-100 transition-colors"
                data-testid="btn-request-whitepaper"
              >
                Request Whitepaper
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Open Letter CTA */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="caption text-cyan-700 mb-4">Open Letter</p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-6">
            A Call for Independent AI Safety Standards and Verification
          </h2>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-10">
            Join governments, researchers, educators, technologists, and institutions worldwide in supporting the development of independent AI safety standards.
          </p>
          <Link to="/open-letter" className="btn-primary" data-testid="btn-open-letter">
            Read & Sign the Open Letter
          </Link>
        </div>
      </section>

      <CertificationInterestForm
        isOpen={showCertForm}
        onClose={() => setShowCertForm(false)}
      />
      <WhitepaperForm
        isOpen={showWhitepaperForm}
        onClose={() => setShowWhitepaperForm(false)}
      />

      {/* First-visit popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="open-letter-popup">
          <div className="bg-white w-full max-w-md shadow-2xl relative">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              data-testid="close-popup"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>
            <div className="bg-slate-900 text-white p-8">
              <p className="text-3xl font-bold tracking-tight mb-2">OPEN LETTER</p>
              <p className="text-base text-slate-300">
                A Call for Independent AI Safety Standards
              </p>
            </div>
            <div className="p-8">
              <p className="text-sm text-slate-700 mb-6">
                Support the development of independent, verifiable AI safety standards. Join researchers, policymakers, educators, and technologists worldwide.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/open-letter"
                  onClick={() => setShowPopup(false)}
                  className="btn-primary w-full text-center"
                >
                  Read & Sign the Letter
                </Link>
                <button
                  onClick={() => setShowPopup(false)}
                  className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Continue browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
